<template>
  <ui-modal-layout :title="tr('Generate new content')" :icon="SparklesIcon">
    <template v-if="hasAvailableCredits">
      <info-text
        v-if="hasNoActiveSubscription"
        type="warning"
        link="/settings/billing-and-payment"
        large
        @click="() => $emit('close')"
      >
        {{ tr('You currently do not have an active subscription!') }}
        {{ tr('To use Infinite∞Creator Lite, select and subscribe to the package that suits you best.') }}
      </info-text>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Content goal') }}</ui-label>
        <ui-select v-model="entity.content_goal" :disabled="isDisabled">
          <ui-select-trigger class="w-full" :aria-label="tr('Content goal')">
            <ui-select-value :placeholder="tr('Select your content goal')" />
          </ui-select-trigger>
          <ui-select-content>
            <ui-select-item v-for="goal in contentGoalOptions" :key="goal.value" :value="goal.value">
              {{ tr(goal.label) }}
            </ui-select-item>
          </ui-select-content>
        </ui-select>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label for="creator-lite-website">{{ tr('Website') }} ({{ tr('optional') }})</ui-label>
        <ui-input
          id="creator-lite-website"
          v-model="entity.website"
          :placeholder="tr('Enter your website or landing page URL')"
          :disabled="isDisabled"
          @blur="() => autoFixUrl()"
        />
      </div>

      <ui-button
        variant="outline"
        class="w-full"
        :disabled="isDisabled"
        @click="() => prefillByWebsite()"
      >
        <loader-circle-icon v-if="analyzeInProgress" class="animate-spin" />
        <wand-sparkles-icon v-else />
        {{ tr('Auto-fill based on Website') }}
      </ui-button>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Content language') }}</ui-label>
        <ui-combobox-select
          v-model="entity.language"
          :options="languageOptions"
          option-value="value"
          :option-label="(option) => tr(option.label)"
          :placeholder="tr('Select language')"
          :search-placeholder="tr('Search')"
          :empty-text="tr('No results')"
          :disabled="isDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Content length') }}</ui-label>
        <ui-combobox-select
          v-model="entity.content_length"
          :options="contentLengthOptions"
          option-value="value"
          :option-label="(option) => tr(option.label)"

          :search-placeholder="tr('Search')"
          :empty-text="tr('No results')"
          :disabled="isDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Industry') }}</ui-label>
        <ui-combobox-select
          v-model="entity.industry"
          :options="industryOptions"
          option-value="value"
          :option-label="(option) => tr(option.label)"
          :placeholder="tr('Select your business category')"
          :search-placeholder="tr('Search')"
          :empty-text="tr('No results')"
          :disabled="isDisabled"
        />
        <ui-input
          v-if="entity.industry === 'other'"
          ref="otherIndustryInput"
          v-model="entity.industry_other"
          :placeholder="tr('If your industry is not listed, please specify')"
          :disabled="isDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Style, tone') }}</ui-label>
        <ui-combobox-select
          v-model="entity.tone"
          :options="toneOptions"
          option-value="value"
          :option-label="(option) => tr(option.label)"
          :placeholder="tr('Choose how you want to communicate')"
          :search-placeholder="tr('Search')"
          :empty-text="tr('No results')"
          :disabled="isDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label for="creator-lite-description">{{ tr('Describe in your own words what the content should be about') }}</ui-label>
        <ui-textarea
          id="creator-lite-description"
          v-model="entity.description"
          class="min-h-20"
          :placeholder="tr('Describe the content outline')"
          :disabled="isDisabled"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>
          {{ tr('Add Image to Scene') }}
          <new-badge>{{ tr('New') }}</new-badge>
        </ui-label>
        <p class="m-0 text-xs text-muted-foreground">
          {{ tr('Upload your product, location, or any image to enhance your post. AI will intelligently incorporate it into the perfect social scene - transforming product shots into lifestyle images, enhancing venues with ideal atmosphere, or creatively integrating your visuals into compelling scenes.') }}
        </p>
        <image-input
          v-model="referenceImage"
          :label="tr('Upload your Image')"
          :active-label="tr('Remove Image from Scene')"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Image format') }}</ui-label>
        <ui-radio-group v-model="entity.visual_format" :disabled="isDisabled" class="flex gap-6">
          <label v-for="format in visualFormatOptions" :key="'visual-format--' + format" class="flex cursor-pointer items-center gap-2 text-sm text-foreground">
            <ui-radio-group-item :value="format" />
            {{ format }}
          </label>
        </ui-radio-group>
      </div>

      <image-input
        v-model="logo"
        :label="tr('Upload your Logo')"
        :active-label="tr('Delete uploaded Logo')"
        accept="image/jpeg, image/png, image/webp, image/svg+xml"
      />

      <section class="flex flex-col gap-4 border-t border-border pt-5">
        <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Target audience') }}</h3>
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Gender') }}</ui-label>
          <gender-selector v-model="entity.target_audience_gender" :disabled="isDisabled" />
        </div>
        <div class="flex flex-col gap-1.5">
          <ui-label>{{ tr('Age') }}</ui-label>
          <div class="flex items-center gap-2.5">
            <age-selector v-model="entity.target_audience_age_min" :disabled="isDisabled" />
            –
            <age-selector v-model="entity.target_audience_age_max" :disabled="isDisabled" />
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <video
        muted
        loop
        autoplay
        playsinline
        class="w-full overflow-hidden rounded-xl shadow-md"
      >
        <source src="~@/assets/creator-video.mp4" type="video/mp4" />
      </video>

      <div class="flex flex-col gap-2">
        <h3 class="m-0 text-base font-semibold text-foreground">{{ tr('Out of Lite credits? Upgrade at half price!') }}</h3>
        <p class="m-0" v-html="tr('Switch to <b>Infinite∞Creator with a 50% discount</b> now and forget about limits! Create more content with one generation, post, schedule like a pro, and choose from our design templates.')" />
        <p v-if="creditRefillInfo" class="m-0 text-muted-foreground" v-html="creditRefillInfo" />
      </div>
    </template>

    <template #footer>
      <ui-button v-if="hasAvailableCredits" :disabled="hasNoActiveSubscription || isLoading || analyzeInProgress" @click="() => submit()">
        <loader-circle-icon v-if="isLoading" class="animate-spin" />
        {{ tr('Start generating') }}
      </ui-button>
      <ui-button
        v-else
        as="a"
        href="https://infinite.tools"
        target="_blank"
      >
        {{ tr('Check out the Infinite∞Creator app') }}
        <external-link-icon />
      </ui-button>
    </template>
  </ui-modal-layout>
</template>

<script>
import { ExternalLink, LoaderCircle, Sparkles, WandSparkles } from '@lucide/vue';
import { markRaw } from 'vue';

import { CONTENT_GENERATION_ITEM_CONTENT_LENGTHS } from '@/consts.js';
import ContentGenerationItem from '@/entities/ContentGenerationItem.js';
import CreatorLiteService from '@/services/CreatorLite.js';
import AgeSelector from '@/ui/components/AgeSelector.vue';
import GenderSelector from '@/ui/components/GenderSelector.vue';
import ImageInput from '@/ui/components/ImageInput.vue';
import InfoText from '@/ui/components/InfoText.vue';
import NewBadge from '@/ui/components/NewBadge.vue';
import { formatToStringYMD } from '@/utils/date.js';
import { isValidURL } from '@/utils/validators.js';

export default {
  name: 'CreatorLiteForm',
  components: {
    AgeSelector,
    ExternalLinkIcon: ExternalLink,
    GenderSelector,
    ImageInput,
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    NewBadge,
    WandSparklesIcon: WandSparkles,
  },
  props: {
    onCreated: {
      type: Function,
      required: false,
      default: () => { /* no-op */ },
    },
  },
  data: () => ({
    SparklesIcon: markRaw(Sparkles),
    isLoading: false,
    analyzeInProgress: false,
    entity: new ContentGenerationItem(),
    referenceImage: null,
    logo: null,
  }),
  computed: {
    languageOptions() {
      return this.$store.getters['app/languagesISO'].map(lang => ({
        value: lang.iso_639_1,
        label: lang.name,
      }));
    },
    contentGoalOptions() {
      return this.$store.getters['app/magicToolContentGenerationGoals'];
    },
    industryOptions() {
      return this.$store.getters['app/industries'];
    },
    toneOptions() {
      return this.$store.getters['app/tones'];
    },
    contentLengthOptions() {
      return CONTENT_GENERATION_ITEM_CONTENT_LENGTHS;
    },
    visualFormatOptions() {
      return [ '1:1', '4:5', '9:16' ];
    },
    isDisabled() {
      return this.hasNoActiveSubscription || this.analyzeInProgress;
    },
    hasNoActiveSubscription() {
      return this.$root.currentCompany.current_subscription === null;
    },
    hasAvailableCredits() {
      return this.$root.currentCompany.creator_credits > 0;
    },
    creditRefillInfo() {
      const credits = this.$root.currentCompany.paid_subscription?.package.creator_credits;
      if (!credits) {
        return null;
      }

      return this.tr(
        'According to your currently active subscription, you will receive <b>:count credits</b> for using Infinite∞Creator Lite on <b>:date</b>',
        {
          ':count': credits,
          ':date': formatToStringYMD(this.$root.currentCompany.next_payment_date),
        },
      );
    },
  },
  watch: {
    'entity.industry'(newValue) {
      if (newValue === 'other') {
        this.$nextTick(() => {
          this.$refs.otherIndustryInput.$el.focus();
        });
      }
      else {
        this.entity.industry_other = '';
      }
    },
  },
  created() {
    this.setInitialLanguage();
  },
  methods: {
    setInitialLanguage() {
      this.entity.language = this.$root.language;
    },
    autoFixUrl() {
      if (!isValidURL(this.entity.website)) {
        return;
      }

      if (
        this.entity.website
        && this.entity.website.toLowerCase().indexOf('http') !== 0
      ) {
        this.entity.website = 'https://' + this.entity.website;
      }
    },
    prefillByWebsite() {
      if (this.isLoading || this.analyzeInProgress) {
        return;
      }

      this.$toast.clear();

      if (!this.entity.website || !isValidURL(this.entity.website)) {
        this.$toast.error(this.tr('Please enter a valid URL'));
        return;
      }
      if (!this.entity.content_goal) {
        this.$toast.error(this.tr('Please select the goal of the content you want to create'));
        return;
      }

      this.analyzeInProgress = true;

      CreatorLiteService.analyzeWebsite(this.entity.website, this.entity.content_goal)
        .then(resp => {
          if (resp.language && this.languageOptions.map(opt => opt.value).includes(resp.language)) {
            this.entity.language = resp.language;
          }
          if (resp.industry && this.industryOptions.map(opt => opt.value).includes(resp.industry)) {
            this.entity.industry = resp.industry;

            if (this.entity.industry === 'other') {
              this.entity.industry_other = resp.industry_other;
            }
          }
          if (resp.tone && this.toneOptions.map(opt => opt.value).includes(resp.tone)) {
            this.entity.tone = resp.tone;
          }
          if (resp.post_idea) {
            this.entity.description = resp.post_idea;
          }
          if (resp.target_audience_gender) {
            this.entity.target_audience_gender = resp.target_audience_gender;
          }
          if (resp.target_audience_age_min) {
            this.entity.target_audience_age_min = resp.target_audience_age_min;
          }
          if (resp.target_audience_age_max) {
            this.entity.target_audience_age_max = resp.target_audience_age_max;
          }

          this.$toast.success(this.tr('Website analysis completed successfully'));

          return resp;
        })
        .catch(err => {
          this.error(err);
        })
        .finally(() => {
          this.analyzeInProgress = false;
        });
    },
    validate() {
      this.$toast.clear();

      if (this.entity.website && !isValidURL(this.entity.website)) {
        this.$toast.error(this.tr('Please enter a valid URL'));
        return false;
      }

      if (!this.entity.content_goal) {
        this.$toast.error(this.tr('Please select the goal of the content you want to create'));
        return false;
      }
      if (!this.entity.industry || (this.entity.industry === 'other' && !this.entity.industry_other)) {
        this.$toast.error(this.tr('Please select the industry that the content will relate to'));
        return false;
      }
      if (!this.entity.tone) {
        this.$toast.error(this.tr('Please select the desired tone for the content'));
        return false;
      }
      if (!this.entity.description.trim()) {
        this.$toast.error(this.tr('Please briefly describe what the content should be about'));
        return false;
      }
      if (this.entity.target_audience_age_min > this.entity.target_audience_age_max) {
        this.$toast.error(this.tr('Minimum age cannot be greater than maximum age'));
        return false;
      }

      return true;
    },
    submit() {
      if (this.isLoading || this.analyzeInProgress) {
        return;
      }
      if (!this.validate()) {
        return;
      }

      this.isLoading = true;

      const clonedEntity = new ContentGenerationItem(this.entity);
      if (this.entity.industry === 'other') {
        clonedEntity.industry = this.entity.industry_other;
      }

      const extraParams = {
        referenceImage: this.referenceImage,
        logo: this.logo,
      };
      CreatorLiteService.create(clonedEntity, extraParams)
        .then(resp => {
          const hasAnyItemPreviously = this.$store.getters['creatorLite/hasAnyItem'];

          this.$store.dispatch('creatorLite/addToList', resp);
          this.$toast.success(this.tr('We are already working on it! The generation will be completed within 1-2 minutes.'));
          this.$emit('close');

          this.onCreated(resp);

          this.$root.currentCompany.creator_credits -= 1;

          if (window.fbq) {
            window.fbq('trackCustom', 'ContentCreated');
          }
          if (window.ttq) {
            window.ttq.track('ContentCreated');
          }
          if (window.rdt) {
            window.rdt('track', 'Custom', { customEventName: 'ContentCreated' });
          }
          if (hasAnyItemPreviously) {
            if (window.fbq) {
              window.fbq('trackCustom', 'FirstContentCreated');
            }
            if (window.ttq) {
              window.ttq.track('FirstContentCreated');
            }
            if (window.rdt) {
              window.rdt('track', 'Custom', { customEventName: 'FirstContentCreated' });
            }
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
  },
};
</script>
